import { Col, ConfigProvider, Flex, Row, Select, Skeleton, Space, Tabs } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAgentEnumerations, useGetDashboardGoals } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { UUID } from "../../../../../typings/global";
import { isAgentTipper } from "../../../../agent/utils";
import { useCurrentAgentAccount, useHasPermissions } from "../../../../auth/hooks";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { DashboardGoalType } from "../../../enums";
import { DashboardGoal } from "../../../types";
import { AddGoalCard } from "./cards/AddGoalCard";
import { AddGoalModal } from "./modals/AddGoalModal";
import { BROKER_FIXED_GOALS, TIPPER_FIXED_GOALS } from "./utils";
import { BrokerAgentsWrapper } from "./wrappers/BrokerAgentsWrapper";
import { BrokerCommissionsWrapper } from "./wrappers/BrokerCommissionsWrapper";
import { BrokerLevelWrapper } from "./wrappers/BrokerLevelWrapper";
import { BrokerPointsWrapper } from "./wrappers/BrokerPointsWrapper";
import { BrokerRankWrapper } from "./wrappers/BrokerRankWrapper";
import { BrokerTraineeClientsGoalWrapper } from "./wrappers/BrokerTraineeClientsGoalWrapper";
import { TipperAllTraineesWrapper } from "./wrappers/TipperAllTraineesWrapper";
import { TipperBasicAcademyWrapper } from "./wrappers/TipperBasicAcademyWrapper";
import { TipperCommissionsWrapper } from "./wrappers/TipperCommissionsWrapper";
import { TipperDirectTraineesWrapper } from "./wrappers/TipperDirectTraineesWrapper";
import { TipperPointsWrapper } from "./wrappers/TipperPointsWrapper";
import { TipperStatusWrapper } from "./wrappers/TipperStatusWrapper";

const getDashboardOptionalGoalWrapper = (year: number, agentId: UUID, goal?: DashboardGoal) => {
  switch (goal?.type) {
    case DashboardGoalType.BROKER_COMMISSIONS:
      return <BrokerCommissionsWrapper goal={goal} year={year} agentId={agentId} />;
    case DashboardGoalType.BROKER_LEVEL:
      return <BrokerLevelWrapper goal={goal} year={year} agentId={agentId} />;
    case DashboardGoalType.BROKER_RANK:
      return <BrokerRankWrapper goal={goal} year={year} agentId={agentId} />;
    case DashboardGoalType.TIPPER_BASIC_ACADEMY:
      return <TipperBasicAcademyWrapper goal={goal} year={year} agentId={agentId} />;
    case DashboardGoalType.TIPPER_STATUS:
      return <TipperStatusWrapper goal={goal} year={year} agentId={agentId} />;
    case DashboardGoalType.TIPPER_ALL_TRAINEES:
      return <TipperAllTraineesWrapper goal={goal} year={year} agentId={agentId} />;
    default:
      return <></>;
  }
};

const getYearsFrom2023 = (): number[] => {
  const startYear = 2023;
  const currentYear = dayjs().year();
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);
};

export const DashboardGoalsWidget = () => {
  const { t } = useTranslation();

  const currentAgent = useCurrentAgentAccount()?.agent;

  const [addGoalModalIsOpen, setAddGoalModalIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(() => dayjs().year());
  const [selectedAgentId, setSelectedAgentId] = useState<UUID>(currentAgent?.id ?? "");

  const { data: selectedAgentData } = useGetAgentEnumerations(selectedAgentId ? [selectedAgentId] : []);
  const selectedAgent = selectedAgentData?.[0];

  const hasAdminDashboardGoalsPermission = useHasPermissions(Permission.ADMIN_DASHBOARD_GOALS);

  const isSelectedAgentTipper = selectedAgent ? isAgentTipper(selectedAgent) : false;

  const { data, isFetching } = useGetDashboardGoals({ id: selectedAgentId ?? "", object: { year: selectedYear } });

  const brokerPointsGoal = data?.find((goal) => goal.type === DashboardGoalType.BROKER_POINTS);
  const brokerTraineesGoal = data?.find((goal) => goal.type === DashboardGoalType.BROKER_ALL_TRAINEES);
  const brokerClientsGoal = data?.find((goal) => goal.type === DashboardGoalType.BROKER_CLIENTS);
  const brokerAgentsGoal = data?.find((goal) => goal.type === DashboardGoalType.BROKER_AGENTS);
  const tipperPointsGoal = data?.find((goal) => goal.type === DashboardGoalType.TIPPER_POINTS);
  const tipperDirectTraineesGoal = data?.find((goal) => goal.type === DashboardGoalType.TIPPER_DIRECT_TRAINEES);
  const tipperCommissionsGoal = data?.find((goal) => goal.type === DashboardGoalType.TIPPER_COMMISSIONS);

  const optionalGoals = data?.filter((goal) =>
    isSelectedAgentTipper ? !TIPPER_FIXED_GOALS.includes(goal.type) : !BROKER_FIXED_GOALS.includes(goal.type),
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <Tabs
        style={{ height: "100%" }}
        className="full-height-tabs"
        size="large"
        tabBarExtraContent={
          <ConfigProvider theme={{ token: { borderRadius: 18 } }}>
            <Space size="small">
              {hasAdminDashboardGoalsPermission ? (
                <AgentSelect
                  style={{ width: 250 }}
                  optionsProps={{
                    subordinateTreePath: currentAgent?.accessTreePath,
                    defaultOption: currentAgent,
                  }}
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                />
              ) : undefined}

              <Select
                style={{ width: 100 }}
                options={getYearsFrom2023().map((year) => ({
                  label: year,
                  value: year,
                }))}
                value={selectedYear}
                onChange={setSelectedYear}
              />
            </Space>
          </ConfigProvider>
        }
        items={[
          {
            key: "fixed",
            label: t("dashboard.goals.titles.fixedGoals"),
            style: { height: "100%" },
            children: (
              <Skeleton loading={isFetching} active>
                <Row gutter={24} style={{ height: "100%" }}>
                  <Col span={8}>
                    {isSelectedAgentTipper ? (
                      <TipperPointsWrapper goal={tipperPointsGoal} year={selectedYear} agentId={selectedAgentId} />
                    ) : (
                      <BrokerPointsWrapper goal={brokerPointsGoal} year={selectedYear} agentId={selectedAgentId} />
                    )}
                  </Col>
                  <Col span={8}>
                    {isSelectedAgentTipper ? (
                      <TipperDirectTraineesWrapper
                        goal={tipperDirectTraineesGoal}
                        year={selectedYear}
                        agentId={selectedAgentId}
                      />
                    ) : (
                      <BrokerTraineeClientsGoalWrapper
                        traineesGoal={brokerTraineesGoal}
                        clientsGoal={brokerClientsGoal}
                        year={selectedYear}
                        agentId={selectedAgentId}
                      />
                    )}
                  </Col>
                  <Col span={8}>
                    {isSelectedAgentTipper ? (
                      <TipperCommissionsWrapper
                        goal={tipperCommissionsGoal}
                        year={selectedYear}
                        agentId={selectedAgentId}
                      />
                    ) : (
                      <BrokerAgentsWrapper goal={brokerAgentsGoal} year={selectedYear} agentId={selectedAgentId} />
                    )}
                  </Col>
                </Row>
              </Skeleton>
            ),
          },
          {
            key: "optional",
            label: t("dashboard.goals.titles.optionalGoals"),
            style: { height: "100%" },
            children: (
              <Skeleton loading={isFetching} active>
                <Row gutter={24} style={{ height: "100%" }} justify="start">
                  {optionalGoals
                    ?.filter((goal) => goal.id)
                    ?.map((goal) => (
                      <Col span={8} key={goal.id}>
                        {getDashboardOptionalGoalWrapper(selectedYear, selectedAgentId, goal)}
                      </Col>
                    ))}

                  {optionalGoals && optionalGoals.filter((goal) => goal.id)?.length < 3 ? (
                    <Col span={8}>
                      <AddGoalCard onClick={() => setAddGoalModalIsOpen(true)} />
                    </Col>
                  ) : undefined}
                </Row>
              </Skeleton>
            ),
          },
        ]}
      />

      <AddGoalModal
        isOpen={addGoalModalIsOpen}
        onCancel={() => setAddGoalModalIsOpen(false)}
        year={selectedYear}
        goalTypeOptions={optionalGoals?.filter((goal) => !goal.id).map((goal) => goal.type)}
        agentId={selectedAgentId}
      />
    </Flex>
  );
};
